import {useEffect, useState} from "react";
import {formatDate, formatTimeOnly} from "../MainPage.utils";
import {toaster, Toggle, Whisper} from "rsuite";
import UpdateButton from "../../../shared/UpdateButton/UpdateButton";
import GearIcon from "@rsuite/icons/Gear";
import {PrimaryClockApi} from "../../../../services/PrimaryClock.api";
import moment from "moment/moment";
import NotificationFactory from "../../../../shared/Notifications";
import {getTooltip} from "../../../../shared/Tooltip";
import {
  PrimaryClockSectionProps,
  PrimaryClockSyncStatus,
  PrimaryClockTimeResponse,
} from "../MainPage.model";
import {SynchronizationApi} from "../../../../services/Synchronization.api";

const PrimaryClockSection = ({deviceId, onTabChange}: PrimaryClockSectionProps) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeSource] = useState("NTP");
  const [timeZone, setTimeZone] = useState("UTC+3");
  const [summerTime, setSummerTime] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [syncStatus, setSyncStatus] = useState<PrimaryClockSyncStatus>({
    ntpSyncEnabled: false,
    gpsSyncEnabled: false,
    initialized: false,
    loading: true
  });
  const [ntpLoading, setNtpLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const handleRefreshSection = async () => {
    setRefreshLoading(true);
    try {
      await Promise.all([
        getClockTime(),
        readSyncStatus(),
        readTimezone()
      ]);

      toaster.push(NotificationFactory.success(), {
        placement: "topEnd",
        duration: 3000
      });

    } catch (error) {
      console.error('Ошибка при обновлении данных:', error);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setRefreshLoading(false);
    }
  };

  const readTimezone = async () => {
    setTimezoneLoading(true);
    try {
      const response = await PrimaryClockApi.getTimezone(String(deviceId))

      const timezoneOffset = response.timeZone || 0;
      const timezoneString = `UTC${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`;

      setTimeZone(timezoneString);
      setSummerTime(response.summerTimeEnabled || false);

    } catch (error) {
      console.error('Ошибка при получении временной зоны:', error);
    } finally {
      setTimezoneLoading(false);
    }
  };

  const changeGpsState = async (state: boolean) => {
    setGpsLoading(true);
    try {
      const result = await SynchronizationApi.updateGpsState(String(deviceId), state)

      if (result) {
        setSyncStatus(prev => ({
          ...prev,
          gpsSyncEnabled: state
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setGpsLoading(false);
    }
  }

  const handleSettingsClick = () => {
    if (onTabChange) {
      onTabChange('primary');
    }
  };

  const changeNtpState = async (state: boolean) => {
    setNtpLoading(true);
    try {
      const result = await SynchronizationApi.updateGpsNtpState(String(deviceId), state)

      if (result) {
        setSyncStatus(prev => ({
          ...prev,
          ntpSyncEnabled: state
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setNtpLoading(false);
    }
  }

  const readSyncStatus = async () => {
    try {
      const response = await SynchronizationApi.getSynchronization(String(deviceId))

      setSyncStatus({
        ntpSyncEnabled: response.ntpSyncEnabled || false,
        gpsSyncEnabled: response.gpsSyncEnabled || false,
        initialized: true,
        loading: false
      });

    } catch (error) {
      console.error('Ошибка при получении статуса синхронизации:', error);
      setSyncStatus(prev => ({
        ...prev,
        loading: false
      }));
    }
  };


  const getClockTime = async () => {
    try {
      const result = await PrimaryClockApi.getTime(String(deviceId)) as PrimaryClockTimeResponse;

      if (result?.time) {
        const wallTime = result.time.split('+')[0];
        const timeMoment = moment(wallTime, 'YYYY-MM-DDTHH:mm:ss');

        const currentDateTime = timeMoment.toDate();
        setCurrentTime(currentDateTime);
        setIsActive(true);
      }
    } catch (e) {
      console.error('Ошибка получения времени:', e);
      toaster.push(
        NotificationFactory.error(`Ошибка получения времени`),
        {placement: "topEnd"}
      );
    }
  };


  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isActive) {
      interval = setInterval(() => {
        setCurrentTime(prevTime => {
          const nextTime = new Date(prevTime.getTime() + 1000);
          return nextTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        getClockTime(),
        readSyncStatus(),
        readTimezone()
      ]);
    };

    loadData().then();

    const syncInterval = setInterval(() => {
      loadData().then();
    }, 60000);

    return () => {
      clearInterval(syncInterval);
    };
  }, [deviceId]);

  const getTimezoneDisplay = () => {
    if (timezoneLoading) {
      return "Загрузка...";
    }
    return `${timeSource} ${timeZone}${summerTime ? " (летнее время)" : ""}`;
  };

  return (
    <div className="fieldset-wrapper">
      <div className="legend-title main-legend-title">Первичные часы</div>
      <section className="primary-clock">
        <div className="row">
          <div className="col-md-12 text-end">
            <UpdateButton
              loading={refreshLoading}
              onRefresh={handleRefreshSection}
            />
          </div>
        </div>
        <div className="row align-items-center" style={{padding: '20px', paddingTop: '0px'}}>
          <div className="col-xl-3 col-lg-3 col-md-3 col-sm-12">
            <div className="time-section">
              <div className="time-source-row">
                <h5 className="sync-label">{getTimezoneDisplay()}</h5>
              </div>

              <div className="datetime-display d-flex align-items-center gap-3">
                <h4 className="current-date mb-0">
                  {formatDate(currentTime)}
                </h4>

                <div className="time-input-container">
                  <input
                    readOnly={true}
                    className="settings-form-control relay-time-control time-input form-control"
                    value={formatTimeOnly(currentTime)}
                    style={{width: '100px'}}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-9 col-lg-9 col-md-9 col-sm-12">
            <div className="sync-toggles">
              {(syncStatus.loading || refreshLoading) ? (
                <div className="sync-loading">
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Загрузка статуса синхронизации...
                </div>
              ) : (
                <div className="toggles-container">
                  <div className="sync-toggle-item">
                    <span className="sync-label">Приемник GPS/ГЛОНАСС</span>
                    <Toggle
                      disabled={true}
                      color="blue"
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      loading={gpsLoading}
                      checked={syncStatus.gpsSyncEnabled}
                      onChange={changeGpsState}
                      size="sm"
                    >
                      <span className="toggle-text">
                        Синхронизация по GPS/ГЛОНАСС {syncStatus.gpsSyncEnabled ? "включена" : "отключена"}
                      </span>
                    </Toggle>
                  </div>

                  <div className="sync-toggle-item">
                    <span className="sync-label">Сервер времени</span>
                    <Toggle
                      disabled={true}
                      color="blue"
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      loading={ntpLoading}
                      checked={syncStatus.ntpSyncEnabled}
                      onChange={changeNtpState}
                      size="sm"
                    >
                      <span className="toggle-text">
                        Синхронизация по NTP {syncStatus.ntpSyncEnabled ? "включена" : "отключена"}
                      </span>
                    </Toggle>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="settings">
          <a
            className="settings-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleSettingsClick();
            }}
          >
            <Whisper
              placement="topStart"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('Перейти в настройки')}>
              <GearIcon className="gear-icon"></GearIcon>
            </Whisper>
          </a>
        </div>
      </section>
    </div>
  );
};

export default PrimaryClockSection;
